import { forwardRef } from 'react';
import { Flex, useColorModeValue } from '@chakra-ui/react';
import { DatePicker } from 'src/components/atoms/DatePicker';
import { dayjs } from 'src/libs/dayjs';
import { TimePeriod } from 'src/types';
import { getModelMaxStartAndEndDatePickerDates } from 'src/utils/model';
import { useFilterContext } from '../../../contexts/FilterContext';
import { EntireDateRangeButton } from 'src/components/molecules/DateRangeSelector/AdditionalDateRangeSelectorButton';

type CustomDateRangeProps = {
  onClose?: () => void;
  isOpen?: boolean;
  children?: React.ReactNode;
};

export const CustomDateRange = forwardRef<HTMLDivElement, CustomDateRangeProps>(
  ({ onClose, isOpen, children }, ref) => {
    const { timeRange, updateTimeRange, currentModelVersion } =
      useFilterContext();

    const { datePickerMaxDate, datePickerMinDate } =
      getModelMaxStartAndEndDatePickerDates(currentModelVersion);

    const selectedStartDate = dayjs(timeRange.start).toDate();
    const selectedEndDate = dayjs(timeRange.end).toDate();

    const onDateRangeChange = (startDate: Date, endDate: Date) => {
      updateTimeRange(TimePeriod.CUSTOM, dayjs(startDate), dayjs(endDate));
    };

    const additionalDateRangeButton =
      datePickerMaxDate && datePickerMinDate ? (
        <EntireDateRangeButton
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          minDate={datePickerMinDate}
          maxDate={datePickerMaxDate}
          onDateRangeChange={onDateRangeChange}
        />
      ) : null;

    const bgColor = useColorModeValue(
      'background.light.highest',
      'background.dark.highest',
    );

    return (
      <Flex bg={bgColor} flexDir={'column'} p="4px" ref={ref}>
        {additionalDateRangeButton}
        <Flex>
          <DatePicker
            sx={{ pr: '8px' }}
            minDate={datePickerMinDate}
            maxDate={datePickerMaxDate}
            selectedDate={selectedStartDate}
            onChange={(updatedStart: Date) => {
              onDateRangeChange(updatedStart, selectedEndDate);
            }}
          />
          <DatePicker
            minDate={datePickerMinDate}
            maxDate={datePickerMaxDate}
            selectedDate={selectedEndDate}
            onChange={(updatedEnd: Date) => {
              onDateRangeChange(selectedStartDate, updatedEnd);
            }}
          />
        </Flex>
      </Flex>
    );
  },
);

CustomDateRange.displayName = 'CustomDateRange';
